import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

export interface FeedbackPageContext {
  pageType: string
  entityType?: string
  entityId?: string | number
  extra?: Record<string, unknown>
}

interface FeedbackContextValue {
  pageContext: FeedbackPageContext | null
  setFeedbackContext: (ctx: FeedbackPageContext) => void
  clearFeedbackContext: () => void
}

const FeedbackCtx = createContext<FeedbackContextValue>({
  pageContext: null,
  setFeedbackContext: () => {},
  clearFeedbackContext: () => {},
})

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const [pageContext, setPageContext] = useState<FeedbackPageContext | null>(null)

  const setFeedbackContext = useCallback((ctx: FeedbackPageContext) => {
    setPageContext(ctx)
  }, [])

  const clearFeedbackContext = useCallback(() => {
    setPageContext(null)
  }, [])

  return (
    <FeedbackCtx.Provider value={{ pageContext, setFeedbackContext, clearFeedbackContext }}>
      {children}
    </FeedbackCtx.Provider>
  )
}

export function useFeedbackContext() {
  return useContext(FeedbackCtx)
}

export function useProvideFeedbackContext(ctx: FeedbackPageContext) {
  const { setFeedbackContext, clearFeedbackContext } = useFeedbackContext()
  const extraKey = JSON.stringify(ctx.extra)
  useEffect(() => {
    setFeedbackContext(ctx)
    return () => clearFeedbackContext()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pageType, ctx.entityType, ctx.entityId, extraKey])
}
